import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Grid,
  FormControlLabel,
  Checkbox,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import { RootState } from '../../store';
import {
  fetchEventTypeById,
  createEventType,
  updateEventType,
  setSelectedItem,
  clearValidationErrors,
  setError,
  clearSuccessMessage,
} from '../../store/reducers/eventTypeSlice';
import { EventType } from '../../services/eventTypeApi';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  formControl: {
    marginBottom: theme.spacing(2),
  },
  buttonGroup: {
    marginTop: theme.spacing(3),
    display: 'flex',
    gap: theme.spacing(1),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 200,
  },
}));

const EventTypeEdit: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  
  const {
    selectedItem,
    loading,
    error,
    validationErrors,
    successMessage,
  } = useSelector((state: RootState) => state.eventType);

  const [formData, setFormData] = useState<Partial<EventType>>({
    Name: '',
    Description: '',
    Active: true,
  });

  // Load Event Type data if editing
  useEffect(() => {
    if (id) {
      dispatch(fetchEventTypeById(parseInt(id)));
    } else {
      dispatch(setSelectedItem(null));
      setFormData({
        Name: '',
        Description: '',
        Active: true,
      });
    }
    
    dispatch(clearValidationErrors());
    dispatch(setError(null));
    dispatch(clearSuccessMessage());
  }, [id, dispatch]);

  // Update form data when selected item changes
  useEffect(() => {
    if (selectedItem && id) {
      setFormData({
        Name: selectedItem.Name,
        Description: selectedItem.Description || '',
        Active: selectedItem.Active,
      });
    }
  }, [selectedItem, id]);

  // Auto-clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        dispatch(clearSuccessMessage());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, dispatch]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    
    // Clear validation errors when user starts typing
    if (validationErrors.length > 0) {
      dispatch(clearValidationErrors());
    }
    if (error) {
      dispatch(setError(null));
    }
  };

  const handleSave = () => {
    if (id) {
      dispatch(updateEventType(parseInt(id), formData));
    } else {
      dispatch(createEventType(formData as Omit<EventType, 'Id'>));
    }
  };

  const handleCancel = () => {
    navigate('/catalogs/eventtype');
  };

  if (loading || (id && !selectedItem)) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Typography variant="h4" gutterBottom>
        {id ? 'Edit Event Type' : 'Add New Event Type'}
      </Typography>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" style={{ marginBottom: 16 }}>
          {error}
        </Alert>
      )}

      {/* Validation Errors */}
      {validationErrors && validationErrors.length > 0 && (
        <Alert severity="error" style={{ marginBottom: 16 }}>
          {validationErrors.map((err: string, index: number) => (
            <div key={index}>{err}</div>
          ))}
        </Alert>
      )}

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" style={{ marginBottom: 16 }}>
          {successMessage}
        </Alert>
      )}

      {/* Form */}
      <Paper className={classes.paper}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Event Type Name"
              name="Name"
              value={formData.Name}
              onChange={handleInputChange}
              required
              className={classes.formControl}
              helperText="Enter the event type name (required)"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  name="Active"
                  checked={formData.Active}
                  onChange={handleInputChange}
                  color="primary"
                />
              }
              label="Active"
              className={classes.formControl}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              name="Description"
              value={formData.Description}
              onChange={handleInputChange}
              multiline
              rows={4}
              className={classes.formControl}
              helperText="Enter a description for the event type (optional)"
            />
          </Grid>
        </Grid>

        {/* Action Buttons */}
        <Box className={classes.buttonGroup}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
          <Button
            variant="outlined"
            onClick={handleCancel}
            disabled={loading}
          >
            Cancel
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default EventTypeEdit; 